import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { businessAPI, paymentsAPI } from "../lib/api";
import { Button } from "../components/ui/button";
import {
  Building2, Package, Eye, MessageSquare, Gavel, Star, AlertTriangle,
  ShieldCheck, FileText, Download, CreditCard, Loader2, Plus, Receipt, TrendingUp,
} from "lucide-react";
import { toast } from "sonner";

export default function BusinessDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    if (!user) return;
    if (user.account_type !== "business") {
      navigate("/profilo");
      return;
    }
    (async () => {
      try {
        const [d, inv] = await Promise.all([businessAPI.dashboard(), businessAPI.invoices()]);
        setData(d);
        setInvoices(inv);
      } catch (e) {
        toast.error("Errore caricamento dashboard");
      } finally {
        setLoading(false);
      }
    })();
  }, [user, navigate]);

  const isIT = user?.country === "IT";
  const subAmount = isIT ? "29,99" : "9,99";
  const subPkgId = isIT ? "business_subscription_year_it" : "business_subscription_year";

  const renewSubscription = async () => {
    setSubscribing(true);
    try {
      const { url } = await paymentsAPI.createBusinessSubscription(window.location.origin, subPkgId);
      if (url) window.location.href = url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore pagamento");
      setSubscribing(false);
    }
  };

  const downloadPdf = async (tx) => {
    setDownloadingId(tx.id);
    try {
      const blob = await businessAPI.downloadInvoicePdf(tx.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ricevuta-smm-${(tx.id || "").slice(0, 8).toUpperCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Errore download PDF");
    } finally {
      setDownloadingId(null);
    }
  };

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-4 py-20 text-center">
        <h2 className="text-2xl font-bold text-[#1d3d3a]">Accedi per visualizzare la dashboard</h2>
        <Link to="/login" className="mt-4 inline-block">
          <Button className="bg-[#e86a24] hover:bg-[#c8561a]">Accedi</Button>
        </Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-20 text-center text-slate-500">
        <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3 text-[#e86a24]" />
        Caricamento dashboard…
      </div>
    );
  }

  const b = data?.business || {};
  const s = data?.stats || {};
  const subActive = b.subscription_active;
  const expiringSoon = subActive && b.days_remaining <= 30;

  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="business-dashboard-page">
      {/* Hero */}
      <div className="bg-gradient-to-br from-[#1d3d3a] via-[#244c48] to-[#0e2422] rounded-3xl p-7 text-white relative overflow-hidden mb-6">
        <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-amber-400/20 blur-3xl" />
        <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-5">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-2xl bg-amber-400 text-[#1d3d3a] flex items-center justify-center shrink-0">
              <Building2 className="w-7 h-7" />
            </div>
            <div>
              <div className="text-[11px] font-black uppercase tracking-[0.3em] text-amber-300">Dashboard Azienda</div>
              <h1 className="text-3xl md:text-4xl font-black mt-1">{b.company_name || user.name}</h1>
              <div className="text-amber-50/80 text-sm mt-1">
                P.IVA / COE: <strong>{b.vat_number || "—"}</strong> · {b.castello || "—"}
              </div>
            </div>
          </div>
          <div className={`rounded-2xl px-4 py-3 border-2 ${subActive ? "bg-emerald-500/20 border-emerald-400/40" : "bg-amber-500/20 border-amber-400/50"}`}>
            <div className="flex items-center gap-2 text-amber-50 text-xs font-bold uppercase tracking-wider">
              {subActive ? <ShieldCheck className="w-4 h-4 text-emerald-300" /> : <AlertTriangle className="w-4 h-4 text-amber-300" />}
              {subActive ? "Iscrizione attiva" : "Iscrizione necessaria"}
            </div>
            <div className="text-white font-black text-lg mt-1">
              {subActive ? (
                <>{b.days_remaining} giorni rimanenti</>
              ) : (
                <>Non attiva</>
              )}
            </div>
            {b.subscription_until && (
              <div className="text-amber-100/80 text-[11px]">
                Scade il {new Date(b.subscription_until).toLocaleDateString("it-IT")}
              </div>
            )}
            {(!subActive || expiringSoon) && (
              <Button
                onClick={renewSubscription}
                disabled={subscribing}
                size="sm"
                className="mt-2 bg-amber-400 hover:bg-amber-500 text-[#1d3d3a] font-black h-8 rounded-full"
                data-testid="dashboard-renew-btn"
              >
                {subscribing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <CreditCard className="w-3.5 h-3.5 mr-1" />}
                {subActive ? "Rinnova €9,99" : "Attiva €9,99"}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="dashboard-stats-grid">
        <StatCard icon={Package} value={s.total_listings} label="Annunci totali" color="orange" />
        <StatCard icon={TrendingUp} value={s.active_listings} label="Annunci attivi" color="emerald" />
        <StatCard icon={Eye} value={s.total_views} label="Visualizzazioni" color="blue" />
        <StatCard icon={MessageSquare} value={s.offers_received} label="Offerte ricevute" color="amber" />
        <StatCard icon={Gavel} value={s.auctions} label="Aste create" color="purple" />
        <StatCard icon={Star} value={s.featured_listings} label="In Vetrina" color="rose" />
        <StatCard icon={AlertTriangle} value={s.pending_payment_listings} label="Pending €0,50" color="amber" />
        <StatCard icon={Receipt} value={`€${(s.total_spent_eur || 0).toFixed(2)}`} label="Totale speso" color="slate" />
      </div>

      {/* CTA pubblica */}
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <Link
          to="/pubblica"
          className="bg-white border-2 border-[#e86a24] rounded-2xl p-5 hover:bg-orange-50 transition-colors group flex items-center gap-4"
          data-testid="dashboard-publish-cta"
        >
          <div className="w-12 h-12 rounded-xl bg-[#e86a24] text-white flex items-center justify-center group-hover:scale-110 transition-transform">
            <Plus className="w-6 h-6" />
          </div>
          <div>
            <div className="font-black text-[#1d3d3a]">Pubblica un nuovo annuncio</div>
            <div className="text-xs text-slate-600">€0,50 per annuncio · pagamento immediato</div>
          </div>
        </Link>
        <Link
          to="/profilo"
          className="bg-white border-2 border-slate-200 rounded-2xl p-5 hover:bg-slate-50 transition-colors flex items-center gap-4"
        >
          <div className="w-12 h-12 rounded-xl bg-slate-100 text-slate-600 flex items-center justify-center">
            <Package className="w-6 h-6" />
          </div>
          <div>
            <div className="font-black text-[#1d3d3a]">Gestisci i tuoi annunci</div>
            <div className="text-xs text-slate-600">Modifica, promuovi o paga annunci pending</div>
          </div>
        </Link>
      </div>

      {/* Invoices table */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="dashboard-invoices">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#1d3d3a]" />
            <h2 className="font-black text-[#1d3d3a]">Ricevute di pagamento</h2>
          </div>
          <div className="text-xs text-slate-500">{invoices.length} ricevut{invoices.length === 1 ? "a" : "e"}</div>
        </div>

        {invoices.length === 0 ? (
          <div className="p-10 text-center text-slate-500 text-sm">
            Nessuna ricevuta. Le tue prossime transazioni Stripe appariranno qui in formato PDF scaricabile.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left p-3 font-bold">Data</th>
                  <th className="text-left p-3 font-bold">Descrizione</th>
                  <th className="text-right p-3 font-bold">Importo</th>
                  <th className="text-right p-3 font-bold">PDF</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`invoice-row-${inv.id}`}>
                    <td className="p-3 text-slate-700">
                      {inv.fulfilled_at ? new Date(inv.fulfilled_at).toLocaleDateString("it-IT") : "—"}
                      <div className="text-[10px] text-slate-400 font-mono">{(inv.id || "").slice(0, 8).toUpperCase()}</div>
                    </td>
                    <td className="p-3 text-slate-800 font-medium">
                      {inv.label}
                      {inv.listing_id && <div className="text-[10px] text-slate-400 font-mono">Annuncio: {inv.listing_id.slice(0, 8)}…</div>}
                    </td>
                    <td className="p-3 text-right font-black text-[#1d3d3a]">€ {Number(inv.amount || 0).toFixed(2)}</td>
                    <td className="p-3 text-right">
                      <Button
                        onClick={() => downloadPdf(inv)}
                        disabled={downloadingId === inv.id}
                        size="sm"
                        variant="outline"
                        className="h-8 px-3 text-xs rounded-full border-[#e86a24] text-[#e86a24] hover:bg-orange-50"
                        data-testid={`invoice-pdf-${inv.id}`}
                      >
                        {downloadingId === inv.id ? (
                          <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                        ) : (
                          <Download className="w-3.5 h-3.5 mr-1" />
                        )}
                        PDF
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, value, label, color }) {
  const palettes = {
    orange: "from-orange-50 to-orange-100 text-[#e86a24]",
    emerald: "from-emerald-50 to-emerald-100 text-emerald-700",
    blue: "from-blue-50 to-blue-100 text-blue-700",
    amber: "from-amber-50 to-amber-100 text-amber-700",
    purple: "from-purple-50 to-purple-100 text-purple-700",
    rose: "from-rose-50 to-rose-100 text-rose-700",
    slate: "from-slate-50 to-slate-100 text-slate-700",
  };
  const cls = palettes[color] || palettes.slate;
  return (
    <div className={`bg-gradient-to-br ${cls.split(" ").slice(0, 2).join(" ")} border border-slate-200 rounded-2xl p-4`}>
      <div className={`w-9 h-9 rounded-lg bg-white/70 flex items-center justify-center mb-2 ${cls.split(" ").slice(2).join(" ")}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="text-2xl font-black text-[#1d3d3a]">{value ?? 0}</div>
      <div className="text-[11px] text-slate-600 font-semibold uppercase tracking-wider mt-0.5">{label}</div>
    </div>
  );
}
